import { useState, useEffect, useCallback } from 'react';
import { Bell, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import pushNotificationService from '../services/pushNotificationService';
import { storage } from '../utils/storage';

/**
 * EnableNotifications
 * -------------------
 * Opt-in banner for native push notifications (WhatsApp-style background
 * notifications). Only appears after the user logs in - the browser permission
 * prompt is NEVER shown on page load; it is only requested after the user
 * explicitly clicks "Enable Notifications".
 *
 * States handled: default / granted / denied / unsupported.
 */
const DISMISS_KEY = 'push-banner-dismissed';

const EnableNotifications = ({ user }) => {
  const [supported] = useState(() =>
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window,
  );
  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'unsupported',
  );
  const [enabling, setEnabling] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );
  const [justEnabled, setJustEnabled] = useState(false);

  // Keep permission state in sync with browser changes.
  useEffect(() => {
    if (!supported) return;
    const refresh = () => setPermission(Notification.permission);
    refresh();
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [supported]);

  // If permission was already granted previously, silently re-sync the
  // subscription (no prompt). This heals subscriptions after SW updates and
  // makes delivery work across sessions.
  const syncSubscription = useCallback(async () => {
    if (!supported || Notification.permission !== 'granted' || !user) return;
    try {
      await pushNotificationService.subscribe(user.id);
    } catch (error) {
      console.error('Failed to sync push subscription:', error);
    }
  }, [supported, user]);

  useEffect(() => {
    syncSubscription();
  }, [syncSubscription]);

  // Register SW helper info (auth token + API base) so notification clicks can
  // mark notifications read server-side.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const postToSw = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        registration.active?.postMessage({
          type: 'API_BASE',
          url: import.meta.env.VITE_API_URL || 'https://srv1309580.hstgr.cloud/puffin/api/v1',
        });
        const token = storage.getItem('token') || '';
        registration.active?.postMessage({ type: 'AUTH_TOKEN', token });
      } catch (error) {
        // Best-effort only.
      }
    };
    postToSw();
    const interval = setInterval(postToSw, 30000);
    return () => clearInterval(interval);
  }, []);

  const enableNotifications = async () => {
    setEnabling(true);
    try {
      const granted = await pushNotificationService.requestPermission();
      setPermission(Notification.permission);
      if (granted && user) {
        await pushNotificationService.subscribe(user.id);
        setJustEnabled(true);
        setTimeout(() => setJustEnabled(false), 4000);
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setEnabling(false);
    }
  };

  const dismiss = () => {
    // Only persist the dismissal once the user has actually made a decision
    // (granted/denied). Dismissing while permission is still 'default' means
    // they closed the banner without deciding - don't hide it forever, or
    // they'd have no way back in and would silently never get push at all.
    if (permission !== 'default') {
      localStorage.setItem(DISMISS_KEY, '1');
    }
    setDismissed(true);
  };

  if (dismissed || !user) return null;

  // Already enabled - nothing to show.
  if (permission === 'granted' && !justEnabled) return null;

  if (permission === 'unsupported') {
    return (
      <div className="w-full bg-gray-800 text-gray-200 text-sm px-4 py-2 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber-400" />
          Push notifications are not supported on this browser.
        </span>
        <button onClick={dismiss} aria-label="Dismiss" className="hover:opacity-70">
          <X size={16} />
        </button>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="w-full bg-amber-50 border-b border-amber-200 text-amber-800 text-sm px-4 py-2 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <AlertTriangle size={14} />
          Notifications are blocked by your browser. Enable them in your
          browser settings to get notifications when you're away from the ERP.
        </span>
        <button onClick={dismiss} aria-label="Dismiss" className="hover:opacity-70">
          <X size={16} />
        </button>
      </div>
    );
  }

  // permission === 'default' (or 'granted' right after enabling)
  return (
    <div className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm px-4 py-2.5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <Bell size={18} className="shrink-0 animate-pulse" />
        <div className="min-w-0">
          <p className="font-medium">
            {justEnabled ? 'Notifications enabled!' : 'Enable Notifications'}
          </p>
          <p className="text-blue-100 text-xs truncate">
            {justEnabled
              ? 'You will now get alerts even when you are not on the ERP.'
              : 'Stay updated about new messages, GMD requests, and procurement activities even when you are away from the ERP.'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {justEnabled ? (
          <span className="flex items-center gap-1 text-emerald-200">
            <CheckCircle2 size={16} />
            Active
          </span>
        ) : (
          <button
            onClick={enableNotifications}
            disabled={enabling}
            className="bg-white text-blue-700 font-medium px-4 py-1.5 rounded-lg hover:bg-blue-50 transition disabled:opacity-60"
          >
            {enabling ? 'Enabling...' : 'Enable Notifications'}
          </button>
        )}
        <button onClick={dismiss} aria-label="Dismiss" className="hover:opacity-70 p-0.5">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default EnableNotifications;